import React from "react";
import { MessageCircle, Phone, MapPin, Clock, Instagram } from "lucide-react";
import { useSite } from "@/lib/api";
import Seo from "@/components/common/Seo";
import SectionHeading from "@/components/common/SectionHeading";

export default function Contact() {
  const { data: site } = useSite();
  const waUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba ${site.brand}, bilgi almak istiyorum.`)}`
    : "#";

  return (
    <div data-testid="contact-page" className="bg-cream">
      <Seo
        title="İletişim"
        description="WhatsApp, telefon ve adres. Aklınızdaki her soru için kasabınıza danışın."
        jsonLd={site ? {
          "@context": "https://schema.org",
          "@type": "Butcher",
          name: "Ertan Kasap",
          image: (process.env.REACT_APP_SITE_URL || window.location.origin) + "/products/scene-hero.webp",
          telephone: site.phone_link || site.phone,
          address: { "@type": "PostalAddress", streetAddress: site.address, addressCountry: "TR" },
          url: (process.env.REACT_APP_SITE_URL || window.location.origin),
          priceRange: "₺₺",
          openingHours: (site.working_hours || []).map((h) => `${h.day} ${h.hours}`),
        } : undefined}
      />
      <section className="border-b border-dashed border-bordo/30 pt-16 pb-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="İletişim" title="Bize Ulaşın" description="Siparişiniz, sorularınız ya da özel istekleriniz için en hızlı yol WhatsApp." />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 border border-[#25D366]/50 bg-[#25D366]/8 p-6 transition-colors hover:bg-[#25D366]/15">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366]">
                <MessageCircle className="h-6 w-6 text-charcoal" />
              </span>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">WhatsApp ile Sipariş</span>
                <span className="font-mono text-lg font-bold text-charcoal">{site?.whatsapp_display}</span>
              </div>
            </a>

            <a href={`tel:${site?.phone_link}`} className="flex items-center gap-4 border border-dashed border-bordo/30 bg-paper p-6 transition-colors hover:border-bordo">
              <span className="flex h-12 w-12 items-center justify-center border border-dashed border-bordo/40">
                <Phone className="h-6 w-6 text-bordo" />
              </span>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">Telefon</span>
                <span className="font-mono text-lg font-bold text-charcoal">{site?.phone}</span>
              </div>
            </a>

            <a href={site?.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 border border-dashed border-bordo/30 bg-paper p-6 transition-colors hover:border-bordo">
              <span className="flex h-12 w-12 items-center justify-center border border-dashed border-bordo/40">
                <Instagram className="h-6 w-6 text-bordo" />
              </span>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">Instagram</span>
                <span className="font-mono text-lg font-bold text-charcoal">{site?.instagram_handle}</span>
              </div>
            </a>

            <div className="flex items-start gap-4 border border-dashed border-bordo/30 bg-paper p-6">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center border border-dashed border-bordo/40">
                <MapPin className="h-6 w-6 text-bordo" />
              </span>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">Adres</span>
                <span className="text-charcoal/90">{site?.address}</span>
              </div>
            </div>

            <div className="flex items-start gap-4 border border-dashed border-bordo/30 bg-paper p-6">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center border border-dashed border-bordo/40">
                <Clock className="h-6 w-6 text-bordo" />
              </span>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">Çalışma Saatleri</span>
                {(site?.working_hours || []).map((wh) => (
                  <span key={wh.day} className="block text-charcoal/90">{wh.day}: <span className="font-mono text-sm text-[#8C857D]">{wh.hours}</span></span>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-hidden border-2 border-charcoal">
            {site?.maps_embed && (
              <iframe title="Ertan Kasap Konum" src={site.maps_embed} className="h-full min-h-[480px] w-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
